import React, { useState } from 'react';

const CsvImport = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  const csvTemplate = `name,scientific_name,price,description,care_level,size,temperament,reef_safe,diet,origin,category,tank_size,stock_quantity,is_available,image_url
Mandarin Dragonet,Synchiropus splendidus,89.99,"Beautiful psychedelic colors",intermediate,3 inches,Peaceful,true,Carnivore,Pacific Ocean,Dragonet,30+ gal,3,true,https://images.unsplash.com/photo-1544551763-46a013bb70d5
Yellow Tang,Zebrasoma flavescens,79.99,"Bright yellow algae eater",beginner,8 inches,Peaceful,true,Herbivore,Hawaii,Tang,75+ gal,5,true,https://images.unsplash.com/photo-1559827260-dc66d52bef19`;

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fish_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let char of lines[i]) {
        if (char === '"' && (current === '' || current[current.length - 1] !== '\\')) {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim().replace(/^"|"$/g, ''));

      if (values.length === headers.length) {
        const fishData = {}; // Initialize fishData object
        headers.forEach((header, index) => { // Iterate over headers to populate fishData
          let value = values[index];
          
          // Convert data types
          if (header === 'price' && value) {
            value = parseFloat(value) || 0;
          } else if (header === 'stock_quantity' && value) {
            value = parseInt(value) || 1;
          } else if (header === 'reef_safe' || header === 'is_available') {
            value = value.toLowerCase() === 'true' || value === '1';
          }
          
          fishData[header] = value;
        });

        // Generate unique ID
        fishData.id = Date.now() + Math.random(); // Assign a unique ID
        fishData.image = getEmojiForFish(fishData.name); // Assign a fallback emoji based on fish name
        
        rows.push(fishData); // Add parsed fish data to rows
      }
    }

    return rows;
  };

  const getEmojiForFish = (fishName) => {
    const name = fishName.toLowerCase();
    if (name.includes('clown')) return '🤡';
    if (name.includes('tang')) return '💙';
    if (name.includes('angel')) return '👼';
    if (name.includes('lion')) return '🦁';
    if (name.includes('dragonet')) return '🐟';
    if (name.includes('cardinal')) return '🪙';
    if (name.includes('trigger')) return '🎨';
    if (name.includes('betta')) return '🌙';
    if (name.includes('firefish')) return '💜';
    return '🐠';
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setImportResults(null);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const previewImport = async () => {
    if (!file) return;

    const text = await file.text();
    try {
      const parsed = parseCSV(text);
      setPreviewData(parsed);
      setShowPreview(true);
    } catch (error) {
      alert('Error parsing CSV: ' + error.message);
    }
  };

  const handleImport = async (confirmedData = null) => {
    if (!file && !confirmedData) return;

    setIsImporting(true);
    
    try {
      let fishToImport;
      
      if (confirmedData) {
        fishToImport = confirmedData;
      } else {
        const text = await file.text();
        fishToImport = parseCSV(text);
      }

      setImportResults({
        success: true,
        count: fishToImport.length,
        data: fishToImport
      });

      // Call parent callback with imported data
      if (onImportComplete) {
        onImportComplete(fishToImport);
      }

      setFile(null);
      setShowPreview(false);
      
    } catch (error) {
      setImportResults({
        success: false,
        error: error.message
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <h3 style={{ 
        fontFamily: 'Playfair Display, serif',
        marginBottom: '20px',
        color: 'var(--tropical-deep)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        📊 CSV Import
      </h3>

      {/* Template Download */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={downloadTemplate}
          className="btn btn-secondary"
          style={{ fontSize: '14px', padding: '8px 16px' }}
        >
          📥 Download CSV Template
        </button>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Download the template to see the required format and example data.
        </p>
      </div>

      {/* File Upload */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
          Select CSV File:
        </label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{
            width: '100%',
            padding: '10px',
            border: '2px dashed var(--tropical-aqua)',
            borderRadius: 'var(--radius)',
            background: 'rgba(0, 206, 201, 0.05)'
          }}
        />
      </div>

      {/* Actions */}
      {file && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={previewImport}
            className="btn btn-secondary"
            style={{ fontSize: '14px' }}
          >
            👁️ Preview Data
          </button>
          <button
            onClick={() => handleImport()}
            disabled={isImporting}
            className="btn"
            style={{ fontSize: '14px' }}
          >
            {isImporting ? 'Importing...' : '📤 Import Fish'}
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ 
            maxWidth: '800px', 
            width: '100%', 
            maxHeight: '80vh', 
            overflow: 'auto',
            margin: 0
          }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--tropical-deep)' }}>
              Preview Import ({previewData.length} fish)
            </h3>
            
            <div style={{ 
              maxHeight: '400px', 
              overflow: 'auto', 
              border: '1px solid #ddd',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Price</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Care Level</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((fish, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>${fish.price}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.care_level}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.stock_quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleImport(previewData)}
                className="btn"
              >
                Confirm Import ({previewData.length} fish)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {importResults && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          background: importResults.success ? 'rgba(85, 239, 196, 0.1)' : 'rgba(253, 121, 168, 0.1)',
          border: `1px solid ${importResults.success ? 'var(--tropical-lime)' : 'var(--tropical-coral)'}`,
          marginTop: '15px'
        }}>
          {importResults.success ? (
            <div>
              <h4 style={{ color: 'var(--tropical-lime)', marginBottom: '8px' }}>
                ✅ Import Successful!
              </h4>
              <p>Successfully imported {importResults.count} fish to your inventory.</p>
            </div>
          ) : (
            <div>
              <h4 style={{ color: 'var(--tropical-coral)', marginBottom: '8px' }}>
                ❌ Import Failed
              </h4>
              <p>Error: {importResults.error}</p>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div style={{ 
        padding: '15px',
        background: 'rgba(0, 206, 201, 0.05)',
        borderRadius: '8px',
        borderLeft: '4px solid var(--tropical-aqua)',
        marginTop: '20px'
      }}>
        <h4 style={{ color: 'var(--tropical-deep)', marginBottom: '10px' }}>📋 CSV Format Requirements:</h4>
        <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', margin: 0, paddingLeft: '20px' }}>
          <li>First row must contain column headers</li>
          <li>Required fields: name, price</li>
          <li>Boolean fields: use 'true'/'false' or '1'/'0'</li>
          <li>Price should be numeric (e.g., 89.99)</li>
          <li>Care levels: 'beginner', 'intermediate', or 'expert'</li>
        </ul>
      </div>
    </div>
  );
};

export default CsvImport;
